import { useState } from 'react';
import { Link } from 'react-router';
import { DonutChart, LineChart, Sparkline } from '../components/charts.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { useNavEntries, usePref } from '../components/nav.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Card, EmptyState, KpiCard, PageHeader, Skeleton } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction } from './manage/common.tsx';

/**
 * Home: how the business is doing, for the person looking. The server's `home.dashboard` asks
 * each module for its figures and keeps out what this person may not see, so the page follows
 * the role by itself. Everything shown is real: an empty business shows an empty page, not
 * examples. Below the figures, what is waiting for this person across the app.
 */

type Range = '7d' | '30d' | '90d';
const RANGES: Range[] = ['7d', '30d', '90d'];

interface Kpi {
  type: 'kpi';
  key: string;
  format: 'money' | 'count';
  value: number;
  previous: number | null;
  good: 'up' | 'down';
  series?: number[];
  count?: number;
}
interface Trend {
  type: 'trend';
  key: string;
  format: 'money' | 'count';
  current: number[];
  previous: number[];
}
interface Parts {
  type: 'parts';
  key: string;
  format: 'money' | 'count';
  shape: 'donut' | 'bars';
  parts: { key: string; label: string | null; value: number }[];
}
type Widget = Kpi | Trend | Parts;
interface Home {
  range: Range;
  today: string;
  from: string;
  to: string;
  days: string[];
  currency: string;
  widgets: Widget[];
}

/** What each figure is called, what it shows and where it leads. */
const KPI: Record<string, { label: MessageKey; icon: IconName; to: string; count?: string }> = {
  sales: { label: 'home.k_sales', icon: 'trendUp', to: '/reports' },
  orders: { label: 'home.k_orders', icon: 'receipt', to: '/receipts' },
  average: { label: 'home.k_average', icon: 'cart', to: '/reports' },
  invoiced: { label: 'home.k_invoiced', icon: 'invoice', to: '/invoices' },
  receivable: { label: 'home.k_receivable', icon: 'cash', to: '/invoices' },
  overdue: { label: 'home.k_overdue', icon: 'alert', to: '/invoices', count: 'home.c_overdue' },
  pipeline: { label: 'home.k_pipeline', icon: 'layers', to: '/crm', count: 'home.c_pipeline' },
  won: { label: 'home.k_won', icon: 'badgeCheck', to: '/crm' },
  purchases: { label: 'home.k_purchases', icon: 'bag', to: '/manage/purchasing?tab=bills' },
  payable: { label: 'home.k_payable', icon: 'bank', to: '/manage/purchasing?tab=bills', count: 'home.c_payable' },
  tickets: { label: 'home.k_tickets', icon: 'lifebuoy', to: '/helpdesk', count: 'home.c_tickets' },
};
const CHART: Record<string, { label: MessageKey; hint: MessageKey; to: string }> = {
  sales_trend: { label: 'home.ch_sales_trend', hint: 'home.ch_sales_trend_hint', to: '/reports' },
  sales_methods: { label: 'home.ch_sales_methods', hint: 'home.ch_sales_methods_hint', to: '/reports' },
  sales_top: { label: 'home.ch_sales_top', hint: 'home.ch_sales_top_hint', to: '/reports' },
};

const shortDay = (d: string) => new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));
const count = (n: number) => new Intl.NumberFormat(locale()).format(n);

function greeting(): MessageKey {
  const h = new Date().getHours();
  return h < 12 ? 'home.morning' : h < 18 ? 'home.afternoon' : 'home.evening';
}

export function Dashboard() {
  const { me, tenant } = useSession();
  const [range, setRange] = usePref<Range>('home.range', '30d');
  const { data, reload } = useAction<Home>('home.dashboard', { range });
  const { entries } = useNavEntries();
  const [refreshing, setRefreshing] = useState(false);
  const loading = !data || data.range !== range;
  const fmt = (format: 'money' | 'count') => (n: number) => (format === 'money' ? money(n) : count(n));
  // In the order of KPI above (sales first), whatever order the modules answered in.
  const order = Object.keys(KPI);
  const kpis = (data?.widgets ?? []).filter((w): w is Kpi => w.type === 'kpi' && !!KPI[w.key]).sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  const charts = (data?.widgets ?? []).filter((w): w is Trend | Parts => w.type !== 'kpi' && !!CHART[w.key]);
  const waiting = entries.filter((e) => e.badge);
  const compare = t(`home.vs_${range}` as MessageKey);
  const firstName = (me?.name ?? '').split(/\s+/)[0] ?? '';

  return (
    <Shell>
      <div className="page home-page">
        <PageHeader
          eyebrow={data ? new Intl.DateTimeFormat(locale(), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${data.today}T00:00:00Z`)) : tenant?.name}
          title={t(greeting(), { name: firstName })}
          subtitle={t('home.subtitle')}
          actions={
            <>
              <div className="segmented" role="radiogroup" aria-label={t('home.period')}>
                {RANGES.map((r) => (
                  <button key={r} type="button" role="radio" aria-checked={range === r} className={range === r ? 'active' : ''} onClick={() => setRange(r)}>
                    {t(`home.r_${r}` as MessageKey)}
                  </button>
                ))}
              </div>
              <Button
                icon="repeat"
                variant="secondary"
                busy={refreshing}
                onClick={async () => {
                  setRefreshing(true);
                  await reload();
                  setRefreshing(false);
                }}
              >
                {t('home.refresh')}
              </Button>
            </>
          }
        />

        {loading ? (
          <div className="home-kpis" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <KpiCard key={i} label="" value="" loading />
            ))}
          </div>
        ) : kpis.length ? (
          <section className="home-kpis" aria-label={t('home.figures')}>
            {kpis.map((k) => {
              const meta = KPI[k.key]!;
              const change = k.previous != null && k.previous > 0 ? ((k.value - k.previous) / k.previous) * 100 : null;
              const note = k.count != null && meta.count ? tn(meta.count, k.count) : k.previous != null ? (change == null && k.previous === 0 ? t('home.no_before') : compare) : t('home.right_now');
              return (
                <KpiCard
                  key={k.key}
                  label={t(meta.label)}
                  value={fmt(k.format)(k.value)}
                  icon={meta.icon}
                  change={change}
                  good={k.good}
                  compare={note}
                  to={meta.to}
                  trend={
                    k.series && k.series.some((v) => v !== 0) ? (
                      <Sparkline values={k.series} color={k.good === 'down' ? 'chart-4' : 'chart-1'} label={t('home.trend_of', { name: t(meta.label) })} />
                    ) : undefined
                  }
                />
              );
            })}
          </section>
        ) : null}

        {!loading && !kpis.length && !charts.length ? (
          <Card>
            <EmptyState
              icon="chart"
              title={t('home.empty_title')}
              body={t('home.empty_body')}
              action={
                <Link to="/apps" className="btn btn-primary btn-md">
                  <Icon name="apps" size={20} />
                  <span className="btn-label">{t('shell.apps')}</span>
                </Link>
              }
            />
          </Card>
        ) : null}

        {!loading && charts.length ? (
          <div className="home-charts">
            {charts.map((c) => {
              const meta = CHART[c.key]!;
              const f = fmt(c.format);
              return (
                <Card
                  key={c.key}
                  title={t(meta.label)}
                  subtitle={t(meta.hint)}
                  className={c.type === 'trend' ? 'home-wide' : ''}
                  actions={
                    <Link className="card-link" to={meta.to}>
                      {t('home.open')}
                      <Icon name="chevronRight" size={16} />
                    </Link>
                  }
                >
                  {c.type === 'trend' ? (
                    <LineChart
                      area
                      labels={data!.days.map(shortDay)}
                      format={f}
                      label={t('home.trend_text', { name: t(meta.label), total: f(c.current.reduce((a, b) => a + b, 0)), before: f(c.previous.reduce((a, b) => a + b, 0)) })}
                      series={[
                        { label: t('home.this_period'), values: c.current },
                        { label: t('home.period_before'), values: c.previous, color: 'chart-6' },
                      ]}
                    />
                  ) : !c.parts.length ? (
                    <p className="muted home-none">{t('home.none_in_period')}</p>
                  ) : c.shape === 'donut' ? (
                    <DonutChart
                      label={t(meta.label)}
                      format={f}
                      total={f(c.parts.reduce((a, p) => a + p.value, 0))}
                      parts={c.parts.map((p) => ({ label: p.label ?? t(`pay.method_${p.key}` as MessageKey), value: p.value }))}
                    />
                  ) : (
                    <RankList parts={c.parts} format={f} />
                  )}
                </Card>
              );
            })}
          </div>
        ) : null}

        {waiting.length ? (
          <Card title={t('home.waiting')} subtitle={t('home.waiting_hint')} flush>
            <ul className="home-waiting">
              {waiting.map((e) => (
                <li key={e.to}>
                  <Link to={e.to} className="home-waiting-row">
                    <span className="home-waiting-icon" aria-hidden="true">
                      <Icon name={e.icon} size={18} />
                    </span>
                    <span className="home-waiting-name">{e.label}</span>
                    <span className="nav-badge" aria-label={e.badgeLabel}>
                      {e.badge! > 99 ? '99+' : e.badge}
                    </span>
                    <Icon name="chevronRight" size={16} />
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
        {loading ? <Skeleton h={260} r="var(--radius-lg)" /> : null}
      </div>
    </Shell>
  );
}

/** A ranking with a bar for each line, longest first. */
function RankList({ parts, format }: { parts: { key: string; label: string | null; value: number }[]; format: (n: number) => string }) {
  const max = Math.max(1, ...parts.map((p) => p.value));
  return (
    <ol className="rank-list">
      {parts.map((p, i) => (
        <li key={p.key}>
          <span className="rank-n">{i + 1}</span>
          <span className="rank-name">{p.label}</span>
          <strong className="rank-value">{format(p.value)}</strong>
          <span className="rank-bar" aria-hidden="true">
            <span style={{ width: `${Math.max(2, (p.value / max) * 100)}%` }} />
          </span>
        </li>
      ))}
    </ol>
  );
}
